import { Box, Grid, Stack, Typography, useMediaQuery } from '@mui/material'
import { FunctionComponent } from 'react'
import { SeriesBar, buildGenericSeriesStack, valueFormatter } from '../../../shared/interfaces/analytics'
import { GraphBarChartStacked } from '../../../components/presentation-components/analytics-components/graphs/graph-bar-chart-stacked'
import { GraphDonutChart } from '../../../components/presentation-components/analytics-components/graphs/graph-donut-chart'
import { GraphBarChart } from '../../../components/presentation-components/analytics-components/graphs/graph-bar-chart'
import gaiaTheme from '../../../root-components/theme'
import { IColorPalatte, IGaiaReportEmissionDetails } from '../shared-services/public-reports.model'
import { GaiaReportWebPageTextFromGraph } from './gaia-report-web-page-text-from-graph'
import { NumericFormat } from 'react-number-format'
import { usePdfExportMode } from '../../../hooks/use-pdf-export-mode'
import { useReportContentOverride } from '../shared-services/gaia-report-content'
import {
  getGaiaReportBodyTextSx,
  getGaiaReportSectionContentSx,
  getGaiaReportSectionFrameSx,
  getGaiaReportSectionHeadingSx,
  getGaiaReportSectionShellSx,
} from './gaia-report-web-page-pdf-styles'

/** API may return a single-basis array or dual { locationBased, marketBased }. */
function dualPieBarData(data: any): { locationBased: any[]; marketBased: any[] } {
  if (Array.isArray(data)) {
    return { locationBased: data, marketBased: data }
  }
  return {
    locationBased: data?.locationBased ?? [],
    marketBased: data?.marketBased ?? [],
  }
}

function dualStacked(data: any): { locationBased: any[]; marketBased: any[] } {
  if (Array.isArray(data)) {
    return { locationBased: data, marketBased: data }
  }
  return {
    locationBased: data?.locationBased ?? [],
    marketBased: data?.marketBased ?? [],
  }
}

function dualOverallScope(overall: any): { locationBased: any; marketBased: any } {
  if (overall?.locationBased != null && overall?.marketBased != null) {
    return { locationBased: overall.locationBased, marketBased: overall.marketBased }
  }
  return { locationBased: overall, marketBased: overall }
}

export const GaiaReportWebPageEmissions: FunctionComponent<{
  screenLabels: any,
  emissionDetails: IGaiaReportEmissionDetails,
  companyName: string,
  reportPeriodCalendarYear: string,
  showSuppliersAndProducts?: boolean,
  colorPalatte: IColorPalatte
}> = ({
  screenLabels,
  emissionDetails,
  companyName,
  reportPeriodCalendarYear,
  showSuppliersAndProducts = true,
  colorPalatte
}) => {

  const isMobileModeMd = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const isMobileModeSm = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const pdfExport = usePdfExportMode();
  const colors = colorPalatte.chartSeries;
  const pdfSummaryFontSize = pdfExport ? '0.95rem' : '1.1rem';
  const metricLabelWidth = pdfExport ? (isMobileModeSm ? '50px' : '88px') : (isMobileModeSm ? '50px' : '100px');
  const metricValueWidth = pdfExport ? (isMobileModeSm ? '125px' : '150px') : (isMobileModeSm ? '125px' : '175px');
  const metricFontSize = pdfExport ? '1rem' : (isMobileModeSm ? '0.85rem' : '1.25rem');
  const metricPadding = pdfExport ? 1.5 : 2;
  const metricRadius = pdfExport ? '16px' : '20px';
  const pdfDetailWidgetHeight = pdfExport ? 308 : '100%';
  const pdfDonutChartHeight = pdfExport ? 188 : 325;
  const pdfVerticalBarChartHeight = pdfExport ? 228 : undefined;
  const pdfStackedBarChartHeight = pdfExport ? 268 : 400;
  const pdfWidgetPadding = pdfExport ? 2.5 : 4;

  const groupPie = dualPieBarData(emissionDetails.groupPieData)
  const categoryBar = dualPieBarData(emissionDetails.categoryBarData)
  const countryPie = dualPieBarData(emissionDetails.countryPieData)
  const supplierPie = dualPieBarData(emissionDetails.supplierPieData)
  const goodBar = dualPieBarData(emissionDetails.goodBarData)
  const stackedScope = dualStacked(emissionDetails.stackedScopeData)
  const stackedLocation = dualStacked(emissionDetails.stackedLocationData)
  const overallDual = dualOverallScope(emissionDetails.overallScopeData)
  const hasCustomMultiplier = Boolean((emissionDetails as any).hasUsedCustomMarketBasedMultiplier)

  const emissionsHeading = useReportContentOverride('emissions.heading')
  const emissionsIntro = useReportContentOverride('emissions.intro')
  const overviewHeading = useReportContentOverride('emissions.overviewHeading')
  const overviewSummary = useReportContentOverride('emissions.overviewSummary', {
    calendarYear: reportPeriodCalendarYear,
    total: Number(overallDual.locationBased.total).toFixed(2),
    scope1: Number(overallDual.locationBased.scope1).toFixed(2),
    scope2: Number(overallDual.locationBased.scope2).toFixed(2),
    scope3: Number(overallDual.locationBased.scope3).toFixed(2),
    marketTotal: Number(overallDual.marketBased.total).toFixed(2),
    marketScope1: Number(overallDual.marketBased.scope1).toFixed(2),
    marketScope2: Number(overallDual.marketBased.scope2).toFixed(2),
    marketScope3: Number(overallDual.marketBased.scope3).toFixed(2),
  })
  const groupsHeading = useReportContentOverride('emissions.groupsHeading')
  const countriesHeading = useReportContentOverride('emissions.countriesHeading')
  const suppliersHeading = useReportContentOverride('emissions.suppliersHeading')

  const overviewSummaryBlock = (
    <>
      {hasCustomMultiplier &&
        <>
          <Typography sx={{ color: colorPalatte.dark, mb: 1, fontSize: pdfSummaryFontSize }}>{overviewSummary ?? <>In {reportPeriodCalendarYear}, {companyName} reported a total of <NumericFormat value={overallDual.locationBased.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e emissions (<NumericFormat value={overallDual.marketBased.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e Market-based), comprising <NumericFormat value={overallDual.locationBased.scope1} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e from Scope 1, <NumericFormat value={overallDual.locationBased.scope2} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e from Scope 2 Location-based (<NumericFormat value={overallDual.marketBased.scope2} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e Scope 2 Market-based) and <NumericFormat value={overallDual.locationBased.scope3} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e from Scope 3.</>}</Typography>
          <Typography sx={{ color: colorPalatte.dark, mb: pdfExport ? 1.5 : 4, fontSize: pdfSummaryFontSize }}>The emission figures presented are based on the market-based approach.</Typography>
        </>}
      {!hasCustomMultiplier && (
        <Typography sx={{ color: colorPalatte.dark, mb: pdfExport ? 1.5 : 4, fontSize: pdfSummaryFontSize }}>
          {overviewSummary ?? <>In {reportPeriodCalendarYear}, {companyName} reported a total of {Number(overallDual.locationBased.total).toFixed(2)} tCO<sub>2</sub>e emissions, comprising {Number(overallDual.locationBased.scope1).toFixed(2)} tCO<sub>2</sub>e from Scope 1, {Number(overallDual.locationBased.scope2).toFixed(2)} tCO<sub>2</sub>e from Scope 2 and {Number(overallDual.locationBased.scope3).toFixed(2)} tCO<sub>2</sub>e from Scope 3.</>}
        </Typography>
      )}
    </>
  )

  const groupsAndCategoriesSection = (
    <>
      <Typography variant='h5' sx={{ color: colorPalatte.dark, mb: 1 }}>{groupsHeading ?? 'Groups and Categories'}</Typography>
      {groupPie.marketBased.length > 0 &&
        <GaiaReportWebPageTextFromGraph
          data={'group'}
          section='emissions'
          emissionDetails={groupPie.marketBased}
          type='pie'
          contentKey='emissions.groupCommentary'
          colorPalatte={colorPalatte} />}
      {categoryBar.marketBased.length > 0 &&
        <Stack sx={{ mt: 2 }}>
          <GaiaReportWebPageTextFromGraph
            data={'category'}
            section='emissions'
            emissionDetails={categoryBar.marketBased}
            type='bar'
            contentKey='emissions.categoryCommentary'
            colorPalatte={colorPalatte} />
        </Stack>}
      <div className="chart-block">
        <Grid container spacing={2} sx={{ mb: pdfExport ? 0 : 6, mt: pdfExport ? 2 : 4 }} alignItems='flex-start'>
          <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <GraphDonutChart
              title='Breakdown of Emissions per Group'
              colors={colors}
              dataset={groupPie.marketBased}
              backgroundColor={'white'}
              chartHeight={pdfDonutChartHeight}
              paperPadding={pdfWidgetPadding}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              paperHeight={pdfDetailWidgetHeight} />
          </Grid>
          <Grid item xl={8} lg={8} md={8} sm={12} xs={12} sx={{ height: '100%' }}>
            <GraphBarChart title='Breakdown of Emissions per Category'
              dataset={categoryBar.marketBased}
              dataKey='label'
              colors={[colorPalatte.dark]}
              series={SeriesBar}
              textColor={colorPalatte.dark}
              layout='vertical'
              backgroundColor={colorPalatte.veryLight}
              chartHeight={pdfVerticalBarChartHeight ?? 400}
              paperPadding={pdfWidgetPadding}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              paperHeight={pdfDetailWidgetHeight} />
          </Grid>
        </Grid>
      </div>
    </>
  );

  const countriesAndLocationsSection = (
    <>
      <Typography variant='h5' sx={{ color: colorPalatte.dark, mb: 2 }}>{countriesHeading ?? `Countries and ${screenLabels.location.pluralCap}`}</Typography>
      {countryPie.marketBased.length > 0 &&
        <GaiaReportWebPageTextFromGraph
          data={'country'}
          section='emissions'
          emissionDetails={countryPie.marketBased}
          type='pie'
          contentKey='emissions.countryCommentary'
          colorPalatte={colorPalatte} />}
      <div className="chart-block">
        <Grid container spacing={2} sx={{ mb: pdfExport ? 0 : 12, mt: pdfExport ? 2 : 4 }} alignItems='flex-start'>
          <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <GraphDonutChart
              colors={colors}
              title='Breakdown of Emissions per Country'
              dataset={countryPie.marketBased}
              backgroundColor={colorPalatte.veryLight}
              textColor={colorPalatte.dark}
              chartHeight={pdfDonutChartHeight}
              paperPadding={pdfWidgetPadding}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              paperHeight={pdfDetailWidgetHeight}
            />
          </Grid>
          <Grid item xl={8} lg={8} md={8} sm={12} xs={12} sx={{ height: '100%' }}>
            <GraphBarChartStacked title={`Detailed View of Monthly ${screenLabels.location.singularCap} Emissions Across the Year`}
              dataset={stackedLocation.marketBased}
              series={buildGenericSeriesStack(stackedLocation.marketBased, valueFormatter)}
              colors={colors}
              backgroundColor={'white'}
              chartHeight={pdfStackedBarChartHeight}
              paperPadding={pdfWidgetPadding}
              paperHeight={pdfDetailWidgetHeight}
            />
          </Grid>
        </Grid>
      </div>
    </>
  );

  const suppliersAndProductsSection = (
    <>
      <Typography variant='h5' sx={{ color: colorPalatte.dark, mb: 2 }}>{suppliersHeading ?? `${screenLabels.supplier.pluralCap} and ${screenLabels.product.pluralCap}`}</Typography>
      {supplierPie.marketBased.length > 0 &&
        <GaiaReportWebPageTextFromGraph
          data={screenLabels.supplier.singular}
          section='emissions'
          emissionDetails={supplierPie.marketBased}
          type='pie'
          contentKey='emissions.supplierCommentary'
          colorPalatte={colorPalatte} />}
      {goodBar.marketBased.length > 0 &&
        <Stack sx={{ mt: 2 }}>
          <GaiaReportWebPageTextFromGraph
            data={screenLabels.product.singular}
            section='emissions'
            emissionDetails={goodBar.marketBased}
            type='bar'
            contentKey='emissions.productCommentary'
            colorPalatte={colorPalatte} />
        </Stack>}
      <div className="chart-block">
        <Grid container spacing={2} sx={{ mb: pdfExport ? 0 : 12, mt: pdfExport ? 2 : 4 }} alignItems='flex-start'>
          <Grid item xl={4} lg={4} md={4} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
            <GraphDonutChart
              colors={colors}
              title={`Breakdown of Emissions per ${screenLabels.supplier.singularCap}`}
              dataset={supplierPie.marketBased}
              backgroundColor={'white'}
              chartHeight={pdfDonutChartHeight}
              paperPadding={pdfWidgetPadding}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              paperHeight={pdfDetailWidgetHeight}
            />
          </Grid>
          <Grid item xl={8} lg={8} md={8} sm={12} xs={12} sx={{ height: '100%' }}>
            <GraphBarChart title={`Breakdown of Emissions per ${screenLabels.product.pluralCap}`}
              dataset={goodBar.marketBased}
              dataKey='label'
              colors={[colorPalatte.dark]}
              series={SeriesBar}
              textColor={colorPalatte.dark}
              layout='vertical'
              backgroundColor={colorPalatte.veryLight}
              chartHeight={pdfVerticalBarChartHeight ?? 400}
              paperPadding={pdfWidgetPadding}
              titleVariant={pdfExport ? 'body2' : 'subtitle1'}
              paperHeight={pdfDetailWidgetHeight} />
          </Grid>
        </Grid>
      </div>
    </>
  );

  if (pdfExport) {
    return (
      <>
        <Stack sx={getGaiaReportSectionFrameSx({ backgroundColor: colorPalatte.veryLight, pdfExport })}>
          <Box sx={getGaiaReportSectionHeadingSx({ backgroundColor: colorPalatte.veryLight, pdfExport })}>
            <Typography variant='h3' sx={{ color: colorPalatte.dark, mb: 2 }}>{emissionsHeading ?? 'Emissions'}</Typography>
          </Box>
          <Box sx={getGaiaReportSectionContentSx()}>
            <Typography sx={getGaiaReportBodyTextSx({ color: colorPalatte.dark, isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 1.25, webMarginBottom: 4 })}>{emissionsIntro ?? `This report provides a summary of ${companyName} GHG emissions evaluation. The findings are based on the data collected, with consideration for its accuracy, categorisation, and validation. This assessment serves as a tool to highlight key areas where improvements can be made to reduce the company’s environmental impact.`}</Typography>
            <Typography variant='h5' sx={{ color: colorPalatte.dark, mb: pdfExport ? 0.5 : 1 }}>{overviewHeading ?? 'Overview'}</Typography>
            {overviewSummaryBlock}
            <div className="chart-block">
              <Grid container spacing={pdfExport ? 1.5 : 2} sx={{ mb: pdfExport ? 1 : 6 }}>
                <Grid item xl={4} lg={4} md={4} sm={12} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                    <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 1</Typography>
                    </Box>
                    <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope1} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                    </Box>
                  </Stack>
                  <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                    <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 2</Typography>
                    </Box>
                    <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope2} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                    </Box>
                  </Stack>
                  <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                    <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 3</Typography>
                    </Box>
                    <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                      <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope3} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                    </Box>
                  </Stack>
                  <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                    <Box sx={{ backgroundColor: 'white', borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                      <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: metricFontSize, textAlign: 'center' }}>Total</Typography>
                    </Box>
                    <Box sx={{ backgroundColor: 'white', borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                      <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xl={8} lg={8} md={8} sm={12} xs={12} sx={{ height: '100%' }}>
                  <GraphBarChartStacked title='Detailed View of Monthly Scope Emissions Across the Year'
                    dataset={stackedScope.marketBased}
                    series={buildGenericSeriesStack(stackedScope.marketBased, valueFormatter)}
                    colors={colors}
                    backgroundColor={'white'}
                    chartHeight={pdfStackedBarChartHeight}
                    paperPadding={pdfExport ? 2.5 : 4} />
                </Grid>
              </Grid>
            </div>
          </Box>
        </Stack>
        <Stack sx={{ ...getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.veryLight, pdfExport }), breakBefore: 'page' }}>
          {groupsAndCategoriesSection}
        </Stack>
        <Stack sx={{ ...getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.veryLight, pdfExport }), breakBefore: 'page' }}>
          {countriesAndLocationsSection}
        </Stack>
        {showSuppliersAndProducts &&
          <Stack sx={{ ...getGaiaReportSectionShellSx({ backgroundColor: colorPalatte.veryLight, pdfExport }), breakBefore: 'page' }}>
            {suppliersAndProductsSection}
          </Stack>}
      </>
    )
  }

  return (
    <Stack sx={getGaiaReportSectionFrameSx({ backgroundColor: colorPalatte.veryLight, pdfExport })}>
      <Box sx={getGaiaReportSectionHeadingSx({ backgroundColor: colorPalatte.veryLight, pdfExport })}>
        <Typography variant='h3' sx={{ color: colorPalatte.dark, mb: 2 }}>Emissions</Typography>
      </Box>
      <Box sx={getGaiaReportSectionContentSx()}>
        <Typography sx={getGaiaReportBodyTextSx({ color: colorPalatte.dark, isMobileModeMd, isMobileModeSm, pdfExport, pdfMarginBottom: 1.25, webMarginBottom: 4 })}>This report provides a summary of {companyName} GHG emissions evaluation. The findings are based on the data collected, with consideration for its accuracy, categorisation, and validation. This assessment serves as a tool to highlight key areas where improvements can be made to reduce the company’s environmental impact.</Typography>
        <Typography variant='h5' sx={{ color: colorPalatte.dark, mb: 1 }}>{overviewHeading ?? 'Overview'}</Typography>
        {overviewSummaryBlock}
        <div className="chart-block">
          <Grid container spacing={2} sx={{ mb: 6 }}>
            <Grid item xl={4} lg={4} md={4} sm={12} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 1</Typography>
                </Box>
                <Box sx={{ backgroundColor: colorPalatte.veryDark, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope1} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Box>
              </Stack>
              <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 2</Typography>
                </Box>
                <Box sx={{ backgroundColor: colorPalatte.dark, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope2} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Box>
              </Stack>
              <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}>Scope 3</Typography>
                </Box>
                <Box sx={{ backgroundColor: colorPalatte.main, borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                  <Typography sx={{ color: 'white', fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.scope3} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Box>
              </Stack>
              <Stack display='flex' flexDirection='row' gap={2} justifyContent='center' alignItems='center' sx={{ mb: 1 }}>
                <Box sx={{ backgroundColor: 'white', borderRadius: metricRadius, p: metricPadding, width: metricLabelWidth }}>
                  <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: metricFontSize, textAlign: 'center' }}>Total</Typography>
                </Box>
                <Box sx={{ backgroundColor: 'white', borderRadius: metricRadius, p: metricPadding, width: metricValueWidth }}>
                  <Typography sx={{ color: 'text.primary', fontWeight: 600, fontSize: metricFontSize, textAlign: 'center' }}><NumericFormat value={overallDual.marketBased.total} displayType={'text'} thousandSeparator="," decimalScale={2} fixedDecimalScale /> tCO<sub>2</sub>e</Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item xl={8} lg={8} md={8} sm={12} xs={12} sx={{ height: '100%' }}>
              <GraphBarChartStacked title='Detailed View of Monthly Scope Emissions Across the Year'
                dataset={stackedScope.marketBased}
                series={buildGenericSeriesStack(stackedScope.marketBased, valueFormatter)}
                colors={colors}
                backgroundColor={'white'}
                chartHeight={pdfStackedBarChartHeight}
                paperPadding={pdfExport ? 2.5 : 4} />
            </Grid>
          </Grid>
        </div>
        {groupsAndCategoriesSection}
        {countriesAndLocationsSection}
        {showSuppliersAndProducts && suppliersAndProductsSection}
      </Box>
    </Stack>
  )
}
